import { AcrValues, ActingType, checkActingOnBehalfOf, checkActingType } from '@oiambk/oiam-oauth-types';

export const filterForcedActingOnBehalfOf = (
  forcedAobo: AcrValues['actingOnBehalfOf'],
  actingOnBehalfOf: AcrValues['actingOnBehalfOf'],
  requiredActingType: AcrValues['actingType'],
): AcrValues['actingOnBehalfOf'] => {
  // no forced-acting-on-behalf-of -> just return the required acting-on-behalf-of
  // it always matches the required acting-type (setConfig check)
  if (!forcedAobo || forcedAobo.length === 0) {
    return actingOnBehalfOf;
  }

  // keep all which startWith at least one of the required actingOnBehalfOfs if any
  // else just take the forcedActingOnBehalfOf
  const filteredAgainstActingOnBehalfOf: string[] =
    actingOnBehalfOf && actingOnBehalfOf.length > 0
      ? forcedAobo.filter(forced => actingOnBehalfOf.filter(aobo => forced.startsWith(aobo)).length > 0)
      : forcedAobo;
  // no required acting-type -> just return the current filter result
  if (!requiredActingType || requiredActingType.length === 0) {
    return filteredAgainstActingOnBehalfOf;
  }

  // filter against required acting-type
  const filteredAgainstActingType: string[] = [];
  for (const bafor of filteredAgainstActingOnBehalfOf) {
    const allowedActingType = getAllowedActingTypes(bafor);
    if (requiredActingType.some(a => allowedActingType.includes(a))) {
      filteredAgainstActingType.push(bafor);
    }
  }

  return filteredAgainstActingType;
};

export const buildAcrValues = (
  opts: AcrValues,
  filterOrType: 'acting-type' | 'login-filter',
  forcedActingOnBehalfOf: AcrValues['actingOnBehalfOf'],
): string => {
  if (!opts && !forcedActingOnBehalfOf?.length) {
    return '';
  }
  const auth = (opts.authnLevel && `authn-level=${opts.authnLevel}`) || null;
  const filtered = filterForcedActingOnBehalfOf(forcedActingOnBehalfOf, opts.actingOnBehalfOf, opts.actingType);
  const aobo =
    (filtered && filtered.length && filterOrType === 'acting-type' && `acting-on-behalf-of=${filtered.join(',')}`) ||
    null;
  //no acting-type in combination with forced-acting-on-behalf-of -> prevent invalid combination at profil selection
  const actt =
    (hasActingType(opts.actingType) &&
      useActingType(filterOrType, forcedActingOnBehalfOf) &&
      `${filterOrType}=${opts.actingType.join(',')}`) ||
    null;

  const authnMethod =
    (filterOrType === 'login-filter' &&
      opts.authnMethod?.length > 0 &&
      !(opts.authnLevel === 'STORK-QAA-Level-3' || opts.authnLevel === 'STORK-QAA-Level-4') &&
      `authn-method=${opts.authnMethod.join(',')}`) ||
    null;

  const forceps =
    (filterOrType === 'acting-type' && opts.forceProfileSelection && 'force-profile-selection=true') || null;

  return [auth, authnMethod, actt, aobo, forceps].filter(s => !!s).join('&');
};

const useActingType = (
  filterOrType: 'acting-type' | 'login-filter',
  forcedActingOnBehalfOf: AcrValues['actingOnBehalfOf'],
): boolean => {
  return filterOrType === 'login-filter' || !forcedActingOnBehalfOf || forcedActingOnBehalfOf.length === 0;
};

const hasActingType = (actingType: AcrValues['actingType']): boolean => {
  return actingType && actingType.length > 0;
};

export type ExternalProfileCheckResult =
  /** cannot continue with external profile */
  | 'incompatible'
  /** could continue, but maybe has to confirm (via force-profile-selection) */
  | 'compatible'
  /** could continue, but eService wants to force-profile-selection if not needed anyway */
  | 'force'
  /** there is a change, but caller can continue with a new JWT */
  | 'irrelevant'
  /** no change detected */
  | 'none';

export const checkOwnAndExternalProfile = ({
  ownCurrent,
  ownRequested,
  externalCurrent,
}: {
  ownCurrent: { actingOnBehalfOf?: string[]; actingType?: ActingType };
  ownRequested: {
    actingOnBehalfOf?: string[];
    actingType?: ActingType[];
    forceProfileSelection?: boolean;
    forcedActingOnBehalfOf?: string[];
  };
  externalCurrent: { actingOnBehalfOf?: string[]; actingType?: ActingType };
}): ExternalProfileCheckResult => {
  const externalMatchesOwnRequestedActingType = checkActingType(externalCurrent.actingType, ownRequested.actingType);
  const externalMatchesOwnRequestedActingOnBehalfOf = checkActingOnBehalfOf(
    externalCurrent.actingOnBehalfOf,
    ownRequested.actingOnBehalfOf,
  );
  if (ownRequested.forcedActingOnBehalfOf?.length) {
    const noProfileSelected = !ownCurrent.actingOnBehalfOf?.length;
    const alreadySelectedAForcedProfile = !!ownCurrent.actingOnBehalfOf?.filter(a =>
      ownRequested.forcedActingOnBehalfOf.includes(a),
    ).length;
    if (noProfileSelected || !alreadySelectedAForcedProfile) {
      // at least not compatible with the force, Luke
      return 'incompatible';
    }
  }
  if (!externalMatchesOwnRequestedActingType || !externalMatchesOwnRequestedActingOnBehalfOf) {
    return 'incompatible';
  }
  const actingOnBehalfOfChanged = actingOnBehalfOfChange(ownCurrent.actingOnBehalfOf, externalCurrent.actingOnBehalfOf);
  const actingTypeChanged = actingTypeChange(ownCurrent.actingType, externalCurrent.actingType);
  if (ownRequested?.actingOnBehalfOf?.length && actingOnBehalfOfChanged) {
    return 'compatible';
  }
  if (ownRequested?.actingType && actingTypeChanged) {
    return 'compatible';
  }
  if (ownRequested.forceProfileSelection) {
    return 'force';
  }
  if (actingOnBehalfOfChanged || actingTypeChanged) {
    return 'irrelevant';
  } else {
    return 'none';
  }
};

const actingTypeChange = (a?: ActingType, b?: ActingType): boolean => {
  return a != b;
};

const actingOnBehalfOfChange = (a?: AcrValues['actingOnBehalfOf'], b?: AcrValues['actingOnBehalfOf']): boolean => {
  if ((!a && b) || (a && !b)) {
    return true;
  }
  if (!a && !b) {
    return false;
  }
  if (a.length != b.length) {
    // case which doesn't occur yet
    return true;
  }
  return !a.every(x => b.includes(x));
};

/**
 * Check, if at least one of the forced-acting-on-behalf-of fulfills the requirements of the eService.
 * It is checked against the required acting-on-behalf-of and the required acting-type.
 */
export const fulfillsAnyForceActingOnBehalfOfRequirements = (
  forcedActingOnBehalfOf: string[],
  requiredActingOnBehalfOf: string[],
  requiredActingType: ActingType[],
) => {
  // if we have no forcedActingOnBehalfOf or no eService requirements we can return true
  if (
    !forcedActingOnBehalfOf ||
    forcedActingOnBehalfOf.length === 0 ||
    ((!requiredActingType || requiredActingType.length === 0) &&
      (!requiredActingOnBehalfOf || requiredActingOnBehalfOf.length === 0))
  ) {
    return true;
  }
  // filter forced-acting-on-behalf-of against eService requirements
  // if there is any match, at least one forced-acting-on-behalf-of fulfills the eService requirements
  const filtered = filterForcedActingOnBehalfOf(forcedActingOnBehalfOf, requiredActingOnBehalfOf, requiredActingType);
  return filtered.length > 0;
};

/**
 * Check, if a given Combination of acting-type and acting-on-behalf-of is valid.
 * All given acting-on-behalf-ofs must match at least one acting-type.
 * All given acting-types must match at least one acting-on-behalf-of.
 */
export const isAllowedCombination = (actingType: ActingType[], actingOnBehalfOf: string[]): boolean => {
  // if there is no combination, it is always valid
  if (!actingType?.length || !actingOnBehalfOf?.length) {
    return true;
  }
  // get all allowed acting-types for all acting-on-behalf-of
  let valid = true;
  const allowedActingTypes = actingOnBehalfOf.flatMap(bafor => {
    // check if every acting-on-behalf-of has at least one acting-type match
    const allowed = getAllowedActingTypes(bafor);
    if (actingType.filter(a => allowed.includes(a)).length === 0) {
      valid = false;
    }
    return allowed;
  });
  // check all given acting-types have at least one match
  return valid && actingType.every(at => allowedActingTypes.includes(at)) && allowedActingTypes.length > 0;
};

/**
 * An acting-on-behalf-of allows only certain acting-type(s) as valid combination.
 *
 * Compare acting-type(s) against acting-on-behalf-of(s)
 * - ...:step:... -> either privatperson or unternehmen
 * - ...:step:person:... -> must contain privatperson
 * - ...:step:betrieb:... -> must contain unternehmen
 * - ...:kiwi:... -> must contain privatperson
 * - ...:pmventerprise:... -> must contain partner
 */
const getAllowedActingTypes = (bafor: string): ActingType[] => {
  if (bafor === 'urn:bafor:step:') {
    return ['privatperson', 'unternehmen'];
  }
  if (bafor.startsWith('urn:bafor:step:betrieb:')) {
    return ['unternehmen'];
  }
  if (bafor.startsWith('urn:bafor:pmventerprise:')) {
    return ['partner'];
  }
  return ['privatperson'];
};
